import {
  adminSettlementDashboard,
  adminPauseSettlement,
  adminResumeSettlement,
  adminRunSettlementOnce,
  getFirstLivePilotStatus,
  getFirstLivePilotChecklist,
  getFirstLivePilotActivationPreview,
  postFirstLivePilotActivateDryRun,
} from "@/lib/api";
import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  adminStats, adminEntities, adminContributions, adminPause, adminDelete, adminVerifyClaim, adminVerifyTx,
  adminGetSettings, adminPutSettings, adminHypeTokens, adminHypeKols,
  adminHypeV2,
} from "@/lib/api";
import {
  adminGetFlipSettings,
  adminPatchFlipSettings,
  adminListFlipRewards,
  adminApproveFlipReward,
  adminRejectFlipReward,
  adminExportFlipRewards,
  adminRebuildFlipReputation,
} from "@/lib/flipApi";
import { fmtUSD, fmtUSDprecise, fmtNum, timeAgo, entityLabel, publicAssetUrl } from "@/lib/format";
import { formatAdminContributionRow } from "@/lib/adminContributionRow";
import HypeRadarV2Section from "@/components/hype/HypeRadarV2Section";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LogOut, Pause, Play, Trash2, BadgeCheck, Loader2, ShieldCheck, Radar, RefreshCw, Copy } from "lucide-react";
import { toast } from "sonner";

const VERIFY_CHAINS = ["ethereum", "base", "arbitrum", "optimism", "bnb",
  "polygon", "avalanche", "solana", "robinhood"];

function SettingsPanel() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { adminGetSettings().then(setS).catch(() => {}); }, []);
  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      const r = await adminPutSettings({
        min_liquidity_usd: Number(s.min_liquidity_usd), min_volume24_usd: Number(s.min_volume24_usd),
        honeypot_check: !!s.honeypot_check, max_sell_tax_pct: Number(s.max_sell_tax_pct),
        refresh_interval_seconds: Number(s.refresh_interval_seconds),
        min_outbid_usd: Number(s.min_outbid_usd), strict_liquidity_gate: !!s.strict_liquidity_gate,
      });
      setS(r); toast.success("Settings saved");
    } catch (e) { toast.error("Failed to save"); } finally { setSaving(false); }
  };
  if (!s) return <Loader2 className="h-5 w-5 animate-spin text-zinc-500" />;
  return (
    <div className="max-w-md space-y-4 rounded-2xl border border-white/10 bg-[#080808] p-5" data-testid="settings-panel">
      <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-400">Anti-spam listing rules</div>
      <div>
        <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Minimum liquidity (USD)</label>
        <Input data-testid="settings-min-liquidity" type="number" className="mt-1 border-white/15 bg-black font-mono"
          value={s.min_liquidity_usd} onChange={(e) => set("min_liquidity_usd", e.target.value)} />
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Minimum outbid to overtake (USD)</label>
        <Input data-testid="settings-min-outbid" type="number" step="0.5" min="0.5" className="mt-1 border-white/15 bg-black font-mono"
          value={s.min_outbid_usd ?? 1} onChange={(e) => set("min_outbid_usd", e.target.value)} />
        <p className="mt-1 text-[10px] text-zinc-600">To take a rank, a token must exceed the one above by at least this amount.</p>
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Minimum 24h volume (USD)</label>
        <Input data-testid="settings-min-volume" type="number" className="mt-1 border-white/15 bg-black font-mono"
          value={s.min_volume24_usd} onChange={(e) => set("min_volume24_usd", e.target.value)} />
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Max buy/sell tax (%)</label>
        <Input data-testid="settings-max-tax" type="number" className="mt-1 border-white/15 bg-black font-mono"
          value={s.max_sell_tax_pct} onChange={(e) => set("max_sell_tax_pct", e.target.value)} />
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Price/liquidity refresh interval (seconds, min 60)</label>
        <Input data-testid="settings-refresh-interval" type="number" min="60" className="mt-1 border-white/15 bg-black font-mono"
          value={s.refresh_interval_seconds ?? 300} onChange={(e) => set("refresh_interval_seconds", e.target.value)} />
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input data-testid="settings-honeypot" type="checkbox" checked={!!s.honeypot_check}
          onChange={(e) => set("honeypot_check", e.target.checked)} />
        Enable honeypot / malicious-token check (EVM)
      </label>
      <label className="flex items-start gap-2 text-sm">
        <input data-testid="settings-strict-gate" type="checkbox" checked={!!s.strict_liquidity_gate}
          onChange={(e) => set("strict_liquidity_gate", e.target.checked)} className="mt-0.5" />
        <span>
          Strict liquidity gate — <b>reject</b> tokens below the liquidity/volume minimums
          <span className="block text-[11px] text-zinc-500">Off (default): below-minimum tokens are listed with a visible warning. The $1 activation deposit remains the real filter.</span>
        </span>
      </label>
      <button data-testid="settings-save" onClick={save} disabled={saving}
        className="flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-bold text-black active:scale-95 disabled:opacity-40">
        {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save settings
      </button>
    </div>
  );
}

function VerifyTxPanel() {
  const [form, setForm] = useState({ chain: "base" });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const run = async () => {
    setLoading(true); setResult(null);
    try {
      const r = await adminVerifyTx(form);
      setResult({ ok: true, ...r });
      toast.success("Transfer verified");
    } catch (e) {
      setResult({ ok: false, error: e.response?.data?.detail || "Failed" });
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-xl space-y-3 rounded-2xl border border-white/10 bg-[#080808] p-5" data-testid="verify-tx-panel">
      <div className="flex items-center gap-2 text-sm font-bold"><ShieldCheck className="h-4 w-4 text-positive" /> Verify a real transaction</div>
      <p className="text-xs text-zinc-500">Paste a real on-chain transfer to confirm the verification engine reads it correctly. Read-only — does not change the leaderboard.</p>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Chain</label>
          <Select value={form.chain} onValueChange={(v) => set("chain", v)}>
            <SelectTrigger data-testid="verify-chain" className="mt-1 border-white/15 bg-black"><SelectValue /></SelectTrigger>
            <SelectContent className="border-white/10 bg-[#0a0a0a]">
              {VERIFY_CHAINS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Token / mint address</label>
          <Input data-testid="verify-token" className="mt-1 border-white/15 bg-black font-mono text-xs" value={form.token || ""} onChange={(e) => set("token", e.target.value)} />
        </div>
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Transaction hash / signature</label>
        <Input data-testid="verify-txhash" className="mt-1 border-white/15 bg-black font-mono text-xs" value={form.tx_hash || ""} onChange={(e) => set("tx_hash", e.target.value)} />
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Treasury / recipient address</label>
        <Input data-testid="verify-treasury" className="mt-1 border-white/15 bg-black font-mono text-xs" value={form.treasury || ""} onChange={(e) => set("treasury", e.target.value)} />
      </div>
      <button data-testid="verify-tx-submit" disabled={loading || !form.tx_hash || !form.token || !form.treasury} onClick={run}
        className="flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-bold text-black active:scale-95 disabled:opacity-40">
        {loading && <Loader2 className="h-4 w-4 animate-spin" />} Verify
      </button>
      {result && (
        <div data-testid="verify-tx-result" className={`rounded-lg border px-3 py-2 font-mono text-xs ${result.ok ? "border-positive/30 text-positive" : "border-negative/30 text-negative"}`}>
          {result.ok ? `✓ Verified — ${result.human} tokens (decimals ${result.decimals})` : `✕ ${result.error}`}
        </div>
      )}
    </div>
  );
}

function fmtCompact(v) {
  if (v == null || isNaN(v)) return "—";
  if (v >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(1)}K`;
  return String(Math.round(v));
}

function fmtEngagementPct(v) {
  if (v == null || Number.isNaN(Number(v))) return null;
  const n = Number(v);
  if (n >= 10) return `${n.toFixed(1)}%`;
  if (n >= 1) return `${n.toFixed(2)}%`;
  return `${n.toFixed(3)}%`;
}

function HypeRadarPanel() {
  const [data, setData] = useState(null);
  const [kols, setKols] = useState(null);
  const [v2, setV2] = useState(null);
  const [loading, setLoading] = useState(false);
  const [kolLoading, setKolLoading] = useState(false);
  const [customHandles, setCustomHandles] = useState("");
  const [sortBy, setSortBy] = useState("score");
  const [sortDir, setSortDir] = useState("desc"); // desc = largest first
  const [chainFilter, setChainFilter] = useState("all");
  const [signalFilter, setSignalFilter] = useState("all");
  const [expandedKol, setExpandedKol] = useState(null);
  const [v2Ecosystem, setV2Ecosystem] = useState("base");

  const toggleColumnSort = (key) => {
    if (sortBy === key) setSortDir((d) => (d === "desc" ? "asc" : "desc"));
    else {
      setSortBy(key);
      setSortDir("desc");
    }
  };

  const loadTokens = async (refresh = false) => {
    setLoading(true);
    try { setData(await adminHypeTokens(refresh)); }
    catch { toast.error("Hype tokens failed"); }
    finally { setLoading(false); }
  };
  const loadKols = async () => {
    setKolLoading(true);
    try {
      setKols(await adminHypeKols(customHandles || undefined));
      setExpandedKol(null);
    }
    catch { toast.error("KOL lookup failed"); }
    finally { setKolLoading(false); }
  };
  const loadV2 = async (ecosystem = v2Ecosystem) => {
    try {
      setV2(await adminHypeV2({ ecosystem, limit: 20, narrative_limit: 15 }));
    } catch {
      setV2(null);
    }
  };

  useEffect(() => {
    loadTokens();
    loadKols();
    loadV2();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const copyAddr = (addr) => { navigator.clipboard?.writeText(addr); toast.success("Address copied"); };

  const chainOptions = useMemo(() => {
    const set = new Set();
    for (const t of data?.tokens || []) {
      if (t.chain) set.add(t.chain);
    }
    return ["all", ...Array.from(set).sort()];
  }, [data]);

  const filteredTokens = useMemo(() => {
    let list = [...(data?.tokens || [])];
    if (chainFilter !== "all") {
      list = list.filter((t) => (t.chain || "") === chainFilter);
    }
    if (signalFilter === "calls") {
      list = list.filter((t) => (t.calls_24h || 0) > 0);
    } else if (signalFilter === "boosts") {
      list = list.filter((t) => (t.boosts || 0) > 0);
    } else if (signalFilter === "has_x") {
      // Real project profile only — not celebrity tweet mentions
      list = list.filter((t) => !!t.x_handle);
    }
    const num = (v) => (v == null || Number.isNaN(Number(v)) ? -1 : Number(v));
    const sortKey = {
      score: (t) => num(t.score),
      price: (t) => num(t.price_usd),
      vol24: (t) => num(t.vol24_usd),
      calls: (t) => num(t.calls_24h),
      txns: (t) => num(t.txns24),
      liquidity: (t) => num(t.liquidity_usd),
      x_followers: (t) => num(t.x_followers),
      boosts: (t) => num(t.boosts),
    }[sortBy] || ((t) => num(t.score));
    const dir = sortDir === "asc" ? 1 : -1;
    list.sort((a, b) => (sortKey(a) - sortKey(b)) * dir);
    return list;
  }, [data, chainFilter, signalFilter, sortBy, sortDir]);

  return (
    <div className="space-y-6">
      <section data-testid="hype-tokens-panel">
        <div className="mb-3 flex items-center gap-3">
          <Radar className="h-4 w-4 text-gold" />
          <span className="text-sm font-bold">Trending meme tokens</span>
          <span className="text-[11px] text-zinc-600">KOL call trackers (TrustKol · Outlight · TrackX · Coral) + DexScreener boosts/volume · CoinGecko trending — candidates to list or invite</span>
          <button data-testid="hype-refresh" onClick={() => loadTokens(true)} disabled={loading}
            className="ml-auto flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-xs hover:bg-white/5 disabled:opacity-40">
            <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
        <div className="mb-3 flex flex-wrap items-center gap-2" data-testid="hype-token-filters">
          <Select value={chainFilter} onValueChange={setChainFilter}>
            <SelectTrigger data-testid="hype-chain-filter" className="h-8 w-[130px] border-white/15 bg-black text-xs">
              <SelectValue placeholder="Chain" />
            </SelectTrigger>
            <SelectContent>
              {chainOptions.map((c) => (
                <SelectItem key={c} value={c}>{c === "all" ? "All chains" : c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={signalFilter} onValueChange={setSignalFilter}>
            <SelectTrigger data-testid="hype-signal-filter" className="h-8 w-[130px] border-white/15 bg-black text-xs">
              <SelectValue placeholder="Signal" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All signals</SelectItem>
              <SelectItem value="calls">Has calls</SelectItem>
              <SelectItem value="boosts">Has boosts</SelectItem>
              <SelectItem value="has_x">Has X profile</SelectItem>
            </SelectContent>
          </Select>
          <span className="font-mono text-[10px] text-zinc-600">{filteredTokens.length} shown</span>
          <span className="font-mono text-[10px] text-zinc-700">click a column to sort</span>
        </div>
        <div className="overflow-hidden rounded-2xl border border-white/10 bg-[#080808]">
          <div className="flex items-center gap-3 border-b border-white/10 px-4 py-2 text-[10px] uppercase tracking-[0.14em] text-zinc-600" data-testid="hype-column-headers">
            <div className="h-7 w-7 shrink-0" />
            <button type="button" onClick={() => toggleColumnSort("score")} data-testid="hype-col-score"
              className={`min-w-0 flex-1 text-left hover:text-zinc-300 ${sortBy === "score" ? "text-zinc-300" : ""}`}>
              Token {sortBy === "score" ? (sortDir === "desc" ? "↓" : "↑") : ""}
            </button>
            {[
              { key: "price", label: "Price", cls: "hidden w-20 sm:block" },
              { key: "vol24", label: "Vol 24h", cls: "w-16" },
              { key: "txns", label: "Tx 24h", cls: "hidden w-14 sm:block" },
              { key: "liquidity", label: "Liquidity", cls: "hidden w-16 md:block" },
              { key: "x_followers", label: "X followers", cls: "hidden w-24 lg:block" },
            ].map((col) => (
              <button key={col.key} type="button" onClick={() => toggleColumnSort(col.key)}
                data-testid={`hype-col-${col.key}`}
                className={`${col.cls} text-right hover:text-zinc-300 ${sortBy === col.key ? "text-zinc-300" : ""}`}>
                {col.label}{sortBy === col.key ? (sortDir === "desc" ? " ↓" : " ↑") : ""}
              </button>
            ))}
            <div className="w-[4.5rem] shrink-0" />
          </div>
          {filteredTokens.map((t) => {
            const chartUrl = t.external_url || t.dex_url;
            const chartLabel = (t.external_provider || "chart").replace(/^\w/, (c) => c.toUpperCase());
            const xUrl = t.x_url || (t.x_handle ? `https://x.com/${t.x_handle}` : null);
            return (
            <div key={`${t.chain}-${t.address}`} className="flex items-center gap-3 border-b border-white/5 px-4 py-2.5 text-sm">
              {t.logo ? (
                <img src={publicAssetUrl(t.logo)} alt="" className="h-7 w-7 rounded-full bg-black object-cover" onError={(e) => { e.currentTarget.style.visibility = "hidden"; }} />
              ) : (
                <div className="h-7 w-7 shrink-0 rounded-full border border-white/10" />
              )}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 font-mono font-bold">
                  {chartUrl ? (
                    <a href={chartUrl} target="_blank" rel="noreferrer" title={`Open on ${chartLabel}`}
                      className="hover:text-white hover:underline">
                      {t.ticker || "?"}
                    </a>
                  ) : (t.ticker || "?")}
                  <span className="rounded border border-white/10 px-1.5 py-0.5 text-[9px] uppercase text-zinc-500">{t.chain}</span>
                  {t.boosts > 0 && <span title="Paid DexScreener boosts" className="rounded border border-gold/40 px-1.5 py-0.5 text-[9px] uppercase text-gold">⚡ {t.boosts}</span>}
                  {t.calls_24h > 0 && (
                    <span title={`KOL calls 24h${t.callers?.length ? ` — ${t.callers.join(" ")}` : ""}${t.last_call_at ? ` · last ${timeAgo(t.last_call_at)}` : ""}`}
                      className="rounded border border-positive/40 px-1.5 py-0.5 text-[9px] uppercase text-positive">
                      call {t.calls_24h}
                    </span>
                  )}
                  {t.x_mention_url && !xUrl && (
                    <a href={t.x_mention_url} target="_blank" rel="noreferrer" title="Mentioned in an X post (not the project account)"
                      className="rounded border border-white/10 px-1.5 py-0.5 text-[9px] uppercase text-zinc-500 hover:text-zinc-300">
                      X mention
                    </a>
                  )}
                </div>
                {chartUrl ? (
                  <a href={chartUrl} target="_blank" rel="noreferrer" title={`Open on ${chartLabel}`}
                    className="block truncate font-mono text-[11px] text-zinc-600 hover:text-zinc-300 hover:underline">
                    {t.address || t.name}
                  </a>
                ) : (
                  <div className="truncate font-mono text-[11px] text-zinc-600">{t.name || t.address}</div>
                )}
              </div>
              <div className="hidden w-20 text-right font-mono text-xs text-zinc-400 sm:block" title="Price USD">
                {t.price_usd == null ? "—" : fmtUSDprecise(t.price_usd)}
              </div>
              <div className="w-16 text-right font-mono text-xs text-zinc-400" title="Volume 24h USD">
                {t.vol24_usd == null ? "—" : `$${fmtCompact(t.vol24_usd)}`}
              </div>
              <div className="hidden w-14 text-right font-mono text-xs text-zinc-500 sm:block" title="Transactions 24h">
                {t.txns24 == null ? "—" : fmtCompact(t.txns24)}
              </div>
              <div className="hidden w-16 text-right font-mono text-xs text-zinc-500 md:block" title="Liquidity USD">
                {t.liquidity_usd == null ? "—" : `$${fmtCompact(t.liquidity_usd)}`}
              </div>
              <div className="hidden w-24 text-right font-mono text-xs text-zinc-500 lg:block" data-testid="hype-x-followers" title="Project X followers">
                {xUrl && t.x_followers != null ? (
                  <a href={xUrl} target="_blank" rel="noreferrer" className="hover:text-zinc-300 hover:underline" title={`@${t.x_handle}`}>
                    {fmtNum(t.x_followers)}
                  </a>
                ) : xUrl ? (
                  <a href={xUrl} target="_blank" rel="noreferrer" className="text-zinc-600 hover:text-zinc-300 hover:underline" title={`@${t.x_handle}`}>
                    @{t.x_handle}
                  </a>
                ) : "—"}
              </div>
              <div className="flex w-[4.5rem] shrink-0 items-center justify-end gap-1">
                <button onClick={() => copyAddr(t.address)} title="Copy address" className="rounded-lg border border-white/15 p-1.5 hover:bg-white/5">
                  <Copy className="h-3.5 w-3.5" />
                </button>
                {chartUrl && (
                  <a href={chartUrl} target="_blank" rel="noreferrer" title={`Open on ${chartLabel}`}
                    className="rounded-lg border border-white/15 px-2 py-1.5 text-[11px] hover:bg-white/5">
                    {t.external_provider === "geckoterminal" ? "GT ↗" :
                     t.external_provider === "gmgn" ? "GMGN ↗" :
                     t.external_provider === "dextools" ? "DT ↗" : "DS ↗"}
                  </a>
                )}
              </div>
            </div>
            );
          })}
          {!loading && filteredTokens.length === 0 && (
            <div className="px-4 py-10 text-center text-sm text-zinc-600">
              {(data?.tokens || []).length === 0 ? "No hype signals right now" : "No tokens match filters"}
            </div>
          )}
          {loading && !data && <div className="flex justify-center px-4 py-10"><Loader2 className="h-5 w-5 animate-spin text-zinc-500" /></div>}
        </div>

        {(data?.coingecko_trending || []).length > 0 && (
          <div className="mt-3 rounded-2xl border border-white/10 bg-[#080808] px-4 py-3">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">CoinGecko trending searches (24h)</div>
            <div className="mt-2 flex flex-wrap gap-2">
              {data.coingecko_trending.map((c) => (
                <span key={c.ticker + c.name} className="rounded-full border border-white/10 px-2.5 py-1 font-mono text-xs text-zinc-300">
                  {c.logo && <img src={publicAssetUrl(c.logo)} alt="" className="mr-1.5 inline h-4 w-4 rounded-full" />}
                  {c.ticker} <span className="text-zinc-600">#{c.mcap_rank ?? "?"}</span>
                </span>
              ))}
            </div>
          </div>
        )}
        {data?.cached_at && <div className="mt-2 text-right font-mono text-[10px] text-zinc-700">cached {timeAgo(data.cached_at)}</div>}
      </section>

      <section data-testid="hype-kols-panel">
        <div className="mb-3 flex items-center gap-3">
          <Radar className="h-4 w-4 text-positive" />
          <span className="text-sm font-bold">KOL engagement</span>
          <span className="text-[11px] text-zinc-600">Public X data — followers, approx engagement rate, recent posts & token mentions</span>
        </div>
        <div className="mb-3 flex gap-2">
          <Input data-testid="hype-handles-input" placeholder="@handle1, @handle2…" value={customHandles}
            onChange={(e) => setCustomHandles(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && customHandles.trim() && loadKols()}
            className="border-white/15 bg-black font-mono text-xs" />
          <button data-testid="hype-kols-submit" onClick={loadKols} disabled={kolLoading}
            className="flex items-center gap-2 rounded-full bg-white px-4 py-2 text-xs font-bold text-black active:scale-95 disabled:opacity-40">
            {kolLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} Lookup
          </button>
        </div>
        <div className="overflow-hidden rounded-2xl border border-white/10 bg-[#080808]">
          {(kols?.kols || []).map((k, i) => {
            const open = expandedKol === k.handle;
            const engLabel = fmtEngagementPct(k.engagement_rate_pct);
            const tokens = k.tokens_pushed || [];
            const posts = k.recent_posts || [];
            return (
              <div key={k.handle} className="border-b border-white/5" data-testid={`hype-kol-row-${k.handle}`}>
                <button
                  type="button"
                  onClick={() => setExpandedKol(open ? null : k.handle)}
                  className="flex w-full items-center gap-3 px-4 py-2.5 text-left hover:bg-white/[0.03]"
                  data-testid={`hype-kol-toggle-${k.handle}`}
                >
                  <span className={`w-6 shrink-0 font-mono text-sm font-bold ${i === 0 ? "text-gold" : "text-zinc-500"}`}>{i + 1}</span>
                  {k.avatar
                    ? <img src={k.avatar} alt="" className="h-7 w-7 shrink-0 rounded-full" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                    : <div className="h-7 w-7 shrink-0 rounded-full border border-white/10" />}
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                      <a href={`https://x.com/${k.handle}`} target="_blank" rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="font-mono text-sm text-zinc-200 hover:text-white">@{k.handle}</a>
                      <span className="truncate text-xs text-zinc-600">{k.name}</span>
                    </div>
                    {tokens.length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1" data-testid={`hype-kol-tokens-${k.handle}`}>
                        {tokens.slice(0, 6).map((t) => (
                          <span key={`${t.kind}-${t.value}`}
                            className="rounded border border-white/10 px-1.5 py-0.5 font-mono text-[9px] uppercase text-zinc-400"
                            title={t.value}>
                            {t.label || t.value}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="shrink-0 text-right">
                    {engLabel ? (
                      <div className="font-mono text-sm font-bold text-positive" title={kols?.engagement_note || "Approximate from public likes/reposts/replies ÷ followers"}>
                        {engLabel} <span className="text-[9px] font-normal text-zinc-600">eng ≈</span>
                      </div>
                    ) : (
                      <div className="font-mono text-[10px] text-zinc-700" title="Recent posts unavailable from public timeline">eng —</div>
                    )}
                    <div className="font-mono text-xs text-zinc-400">{fmtNum(k.followers)} <span className="text-[10px] text-zinc-600">followers</span></div>
                  </div>
                  <span className="w-4 shrink-0 text-center font-mono text-xs text-zinc-600">{open ? "−" : "+"}</span>
                </button>
                {open && (
                  <div className="space-y-2 border-t border-white/5 bg-black/40 px-4 py-3" data-testid={`hype-kol-posts-${k.handle}`}>
                    {posts.length === 0 ? (
                      <div className="text-xs text-zinc-600">
                        Recent posts unavailable via public timeline (fxtwitter). Profile stats may still be accurate.
                      </div>
                    ) : posts.map((p) => (
                      <div key={p.id || p.url || p.snippet} className="flex gap-3 text-xs">
                        <div className="min-w-0 flex-1">
                          {p.url ? (
                            <a href={p.url} target="_blank" rel="noreferrer" className="block text-zinc-300 hover:text-white hover:underline">
                              {p.snippet || p.text || "(no text)"}
                            </a>
                          ) : (
                            <div className="text-zinc-400">{p.snippet || p.text || "(no text)"}</div>
                          )}
                          {(p.tokens || []).length > 0 && (
                            <div className="mt-1 flex flex-wrap gap-1">
                              {p.tokens.map((t) => (
                                <span key={`${p.id}-${t.kind}-${t.value}`}
                                  className="rounded border border-positive/30 px-1.5 py-0.5 font-mono text-[9px] uppercase text-positive/80">
                                  {t.label || t.value}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="shrink-0 text-right font-mono text-[10px] text-zinc-600">
                          <div>{p.created_at ? timeAgo(p.created_at) : "—"}</div>
                          <div className="mt-0.5 text-zinc-500">
                            ♥ {fmtCompact(p.likes)} · ↻ {fmtCompact(p.reposts)} · 💬 {fmtCompact(p.replies)}
                          </div>
                          {p.is_repost && <div className="text-zinc-700">repost</div>}
                        </div>
                      </div>
                    ))}
                    {engLabel && (
                      <div className="pt-1 font-mono text-[10px] text-zinc-700">
                        ≈ {engLabel} engagement · {k.engagement?.posts_sampled || posts.length} posts · public likes+reposts+replies ÷ followers
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          {!kolLoading && (kols?.kols || []).length === 0 && (
            <div className="px-4 py-10 text-center text-sm text-zinc-600">No caller handles yet — list a caller with an X handle</div>
          )}
          {kolLoading && !kols && <div className="flex justify-center px-4 py-10"><Loader2 className="h-5 w-5 animate-spin text-zinc-500" /></div>}
        </div>
        {(kols?.missing || []).length > 0 && (
          <div className="mt-2 font-mono text-[10px] text-zinc-700">not found on X: {kols.missing.map((m) => `@${m}`).join(", ")}</div>
        )}
        {kols?.engagement_note && (
          <div className="mt-2 font-mono text-[10px] text-zinc-700">{kols.engagement_note}</div>
        )}
      </section>

      <HypeRadarV2Section
        v2={v2}
        v2Ecosystem={v2Ecosystem}
        onEcosystemChange={(v) => { setV2Ecosystem(v); loadV2(v); }}
        onRefresh={() => loadV2()}
      />

    </div>
  );
}


function FlipUtilityPanel() {
  const [data, setData] = useState(null);
  const [rewards, setRewards] = useState([]);
  const [saving, setSaving] = useState(false);
  const [rebuildWallet, setRebuildWallet] = useState("");
  const [rebuildChain, setRebuildChain] = useState("ethereum");

  const load = useCallback(async () => {
    try {
      const [s, r] = await Promise.all([
        adminGetFlipSettings(),
        adminListFlipRewards({ limit: 50 }),
      ]);
      setData(s);
      setRewards(r.rewards || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load FLIP settings");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setSetting = (k, v) =>
    setData((p) => ({ ...p, settings: { ...p.settings, [k]: v } }));

  const save = async () => {
    if (!data?.settings) return;
    setSaving(true);
    try {
      const s = data.settings;
      const r = await adminPatchFlipSettings({
        calls_enabled: !!s.calls_enabled,
        campaigns_enabled: !!s.campaigns_enabled,
        premium_analytics_enabled: !!s.premium_analytics_enabled,
        rewards_enabled: !!s.rewards_enabled,
        max_open_calls_per_wallet: Number(s.max_open_calls_per_wallet || 5),
        campaign_creation_tier: s.campaign_creation_tier || "conviction",
      });
      setData((p) => ({ ...p, settings: r.settings }));
      toast.success("FLIP settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const rebuild = async () => {
    if (!rebuildWallet.trim()) return;
    try {
      await adminRebuildFlipReputation(rebuildWallet.trim(), rebuildChain);
      toast.success("Reputation rebuilt");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Rebuild failed");
    }
  };

  const exportRewards = async () => {
    try {
      const batch = await adminExportFlipRewards("APPROVED");
      const blob = new Blob([JSON.stringify(batch, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `flip-rewards-export-${Date.now()}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Export downloaded (manual distribution only)");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Export failed");
    }
  };

  if (!data) return <Loader2 className="h-5 w-5 animate-spin text-zinc-500" />;
  const s = data.settings || {};
  const token = data.token || {};

  return (
    <div className="space-y-6" data-testid="flip-utility-panel">
      <div className="rounded-2xl border border-white/10 bg-[#080808] p-5">
        <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-400">$FLIP Utility</div>
        <p className="mt-2 text-sm text-zinc-500">
          Master flag is env-only (<span className="font-mono text-zinc-400">FLIP_UTILITY_ENABLED</span>).
          Currently:{" "}
          <span className={data.enabled ? "text-positive" : "text-negative"}>
            {data.enabled ? "enabled" : "disabled"}
          </span>
          . Never mutates leaderboard <span className="font-mono">total_backed_usd</span>.
        </p>
        <div className="mt-4 grid gap-2 font-mono text-xs text-zinc-500 sm:grid-cols-2">
          <div>chain: {token.chain || "—"}</div>
          <div>symbol: {token.symbol || "FLIP"}</div>
          <div className="sm:col-span-2 break-all">address: {token.address || "—"}</div>
        </div>
      </div>

      <div className="max-w-md space-y-4 rounded-2xl border border-white/10 bg-[#080808] p-5">
        <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-400">Feature toggles</div>
        {[
          ["calls_enabled", "Conviction Calls"],
          ["campaigns_enabled", "Campaigns"],
          ["premium_analytics_enabled", "Premium analytics"],
          ["rewards_enabled", "Rewards ledger"],
        ].map(([key, label]) => (
          <label key={key} className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              data-testid={`flip-setting-${key}`}
              checked={!!s[key]}
              onChange={(e) => setSetting(key, e.target.checked)}
            />
            {label}
          </label>
        ))}
        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Max open calls / wallet</label>
          <Input
            data-testid="flip-setting-max-calls"
            type="number"
            min="1"
            className="mt-1 border-white/15 bg-black font-mono"
            value={s.max_open_calls_per_wallet ?? 5}
            onChange={(e) => setSetting("max_open_calls_per_wallet", e.target.value)}
          />
        </div>
        <button
          data-testid="flip-settings-save"
          onClick={save}
          disabled={saving}
          className="flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-bold text-black active:scale-95 disabled:opacity-40"
        >
          {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save FLIP settings
        </button>
      </div>

      <div className="max-w-lg space-y-3 rounded-2xl border border-white/10 bg-[#080808] p-5">
        <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-400">Rebuild reputation</div>
        <div className="flex flex-wrap gap-2">
          <Select value={rebuildChain} onValueChange={setRebuildChain}>
            <SelectTrigger className="w-36 border-white/15 bg-black" data-testid="flip-rebuild-chain">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {["ethereum", "base", "solana"].map((c) => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            data-testid="flip-rebuild-wallet"
            placeholder="0x… or solana address"
            className="min-w-[220px] flex-1 border-white/15 bg-black font-mono"
            value={rebuildWallet}
            onChange={(e) => setRebuildWallet(e.target.value)}
          />
          <button
            data-testid="flip-rebuild-run"
            onClick={rebuild}
            className="rounded-full border border-white/15 px-4 py-2 text-xs font-bold hover:bg-white/5"
          >
            Rebuild
          </button>
        </div>
      </div>

      <div className="rounded-2xl border border-white/10 bg-[#080808] p-5">
        <div className="flex items-center justify-between gap-3">
          <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-400">Rewards (export only)</div>
          <button
            data-testid="flip-rewards-export"
            onClick={exportRewards}
            className="rounded-full border border-white/15 px-3 py-1.5 text-xs hover:bg-white/5"
          >
            Export APPROVED
          </button>
        </div>
        <div className="mt-3 space-y-2" data-testid="flip-rewards-list">
          {rewards.map((r) => (
            <div key={r.id} className="flex items-center justify-between border-b border-white/5 py-2 text-sm">
              <div className="min-w-0">
                <div className="font-mono text-xs text-zinc-400 truncate">{r.wallet}</div>
                <div className="text-[10px] uppercase text-zinc-600">{r.status} · {r.amount_display || r.amount || "—"}</div>
              </div>
              <div className="flex gap-1">
                {r.status === "PENDING" && (
                  <>
                    <button
                      className="rounded border border-positive/40 px-2 py-1 text-[10px] text-positive"
                      onClick={async () => { await adminApproveFlipReward(r.id); toast.success("Approved"); load(); }}
                    >
                      Approve
                    </button>
                    <button
                      className="rounded border border-negative/40 px-2 py-1 text-[10px] text-negative"
                      onClick={async () => { await adminRejectFlipReward(r.id); toast.success("Rejected"); load(); }}
                    >
                      Reject
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
          {rewards.length === 0 && <div className="py-8 text-center text-zinc-600">No reward events</div>}
        </div>
      </div>
    </div>
  );
}


function FirstLivePilotPanel() {
  const [status, setStatus] = useState(null);
  const [checklist, setChecklist] = useState(null);
  const [preview, setPreview] = useState(null);
  const [dryRun, setDryRun] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmDanger, setConfirmDanger] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, c] = await Promise.all([getFirstLivePilotStatus(), getFirstLivePilotChecklist()]);
      setStatus(s);
      setChecklist(c);
    } catch (e) {
      toast.error("Failed to load first-pilot status");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadPreview = async () => {
    setBusy(true);
    try {
      setPreview(await getFirstLivePilotActivationPreview());
    } catch (e) {
      toast.error("Preview failed");
    } finally {
      setBusy(false);
    }
  };

  const runDryRun = async () => {
    if (!confirmDanger) {
      toast.error("Confirm environment/chain/asset banner first");
      return;
    }
    setBusy(true);
    try {
      setDryRun(await postFirstLivePilotActivateDryRun());
      toast.success("Dry-run complete — no state changed");
    } catch (e) {
      toast.error("Dry-run failed");
    } finally {
      setBusy(false);
    }
  };

  if (!status || !checklist) {
    return <Loader2 className="h-5 w-5 animate-spin text-zinc-500" />;
  }

  const envBanner = status.environment_banner;
  const ready = !!status.readiness?.ready;
  const blockers = status.readiness?.blockers || [];

  return (
    <div className="space-y-4" data-testid="first-live-pilot-panel">
      {envBanner === "PRODUCTION PILOT" && (
        <div
          className="rounded-xl border border-amber-500/50 bg-amber-500/10 px-4 py-3 text-sm font-bold tracking-wide text-amber-200"
          data-testid="production-pilot-banner"
        >
          PRODUCTION PILOT
        </div>
      )}
      {envBanner && envBanner !== "PRODUCTION PILOT" && (
        <div className="rounded-xl border border-white/15 bg-white/5 px-4 py-2 text-xs uppercase tracking-[0.2em] text-zinc-400" data-testid="pilot-env-banner">
          Environment: {envBanner}
        </div>
      )}

      <div className="rounded-2xl border border-white/10 bg-[#080808] p-5">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-400">First live pilot readiness</div>
            <div className={`mt-1 font-mono text-lg font-bold ${ready ? "text-positive" : "text-negative"}`} data-testid="first-pilot-ready-flag">
              READY_FOR_FIRST_LIVE_PILOT = {ready ? "true" : "false"}
            </div>
            <div className="mt-1 text-xs text-zinc-500">Profile status: {status.profile?.status || "draft"} · Auto mainnet writes: off</div>
          </div>
          <button
            type="button"
            onClick={load}
            data-testid="first-pilot-refresh"
            className="rounded-full border border-white/15 px-3 py-1.5 text-xs hover:bg-white/5"
          >
            <RefreshCw className="inline h-3.5 w-3.5" /> Refresh
          </button>
        </div>

        <div className="mt-4 space-y-2" data-testid="first-pilot-checklist">
          {(checklist.items || []).map((item) => (
            <label key={item.label} className="flex items-center gap-2 text-sm text-zinc-300">
              <input
                type="checkbox"
                readOnly
                checked={!!item.done}
                data-testid={`first-pilot-check-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              />
              <span>{item.label}</span>
              <span className={`ml-auto font-mono text-[10px] uppercase ${item.status === "PASS" ? "text-positive" : item.status === "WARN" ? "text-amber-400" : "text-zinc-500"}`}>
                {item.status}
              </span>
            </label>
          ))}
        </div>
        {!ready && (
          <p className="mt-3 text-xs text-zinc-500" data-testid="first-pilot-not-green">
            No green “ready” until all required BLOCK checks pass.
          </p>
        )}
      </div>

      <div className="rounded-2xl border border-white/10 bg-[#080808] p-5" data-testid="first-pilot-blockers">
        <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-400">Blockers ({blockers.length})</div>
        <ul className="mt-3 space-y-2">
          {blockers.map((b) => (
            <li key={b.code} className="rounded-lg border border-white/5 px-3 py-2 text-sm">
              <span className="font-mono text-xs text-negative">{b.code}</span>
              <span className="ml-2 text-zinc-400">{b.category}</span>
              <div className="text-zinc-300">{b.message}</div>
            </li>
          ))}
          {blockers.length === 0 && <li className="text-sm text-zinc-500">No blockers</li>}
        </ul>
      </div>

      <div className="rounded-2xl border border-white/10 bg-[#080808] p-5" data-testid="first-pilot-action-safety">
        <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-400">Dangerous action confirmation</div>
        <p className="mt-2 text-xs text-zinc-500">Before activation dry-run, confirm environment / chain / asset / amount / contract / pilot id.</p>
        <div className="mt-3 grid gap-2 font-mono text-xs text-zinc-300 sm:grid-cols-2">
          <div>environment: {status.profile?.environment || "—"}</div>
          <div>chain: {status.profile?.chain_id || "—"}</div>
          <div>asset: {status.profile?.reward_asset_address || "—"}</div>
          <div>amount: {status.profile?.pilot_budget_base_units ?? status.profile?.pilot_budget_usd ?? "—"}</div>
          <div>contract: {status.profile?.distributor_address || "—"}</div>
          <div>pilot id: {status.profile?.pilot_id || "—"}</div>
        </div>
        <label className="mt-3 flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={confirmDanger}
            onChange={(e) => setConfirmDanger(e.target.checked)}
            data-testid="first-pilot-confirm-danger"
          />
          I reviewed the fields above
        </label>
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={loadPreview}
            data-testid="first-pilot-activation-preview"
            className="rounded-full border border-white/15 px-3 py-1.5 text-xs"
          >
            Activation preview
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={runDryRun}
            data-testid="first-pilot-activation-dry-run"
            className="rounded-full border border-white/15 px-3 py-1.5 text-xs"
          >
            Dry-run activation
          </button>
        </div>
        {preview && (
          <pre className="mt-3 overflow-auto rounded-lg bg-black/50 p-3 text-[11px] text-zinc-400" data-testid="first-pilot-preview-json">
            {JSON.stringify(preview, null, 2)}
          </pre>
        )}
        {dryRun && (
          <pre className="mt-3 overflow-auto rounded-lg bg-black/50 p-3 text-[11px] text-zinc-400" data-testid="first-pilot-dry-run-json">
            {JSON.stringify(dryRun, null, 2)}
          </pre>
        )}
      </div>
    </div>
  );
}

function SettlementOpsPanel() {
  const [dash, setDash] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try {
      const d = await adminSettlementDashboard();
      setDash(d);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Settlement dashboard unavailable");
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  const pause = async () => {
    setBusy(true);
    try { await adminPauseSettlement(); toast.success("Settlement paused"); load(); }
    catch (e) { toast.error("Pause failed"); }
    finally { setBusy(false); }
  };
  const resume = async () => {
    setBusy(true);
    try { await adminResumeSettlement(); toast.success("Settlement resumed (broadcast still gated)"); load(); }
    catch (e) { toast.error("Resume failed"); }
    finally { setBusy(false); }
  };
  const runOnce = async () => {
    setBusy(true);
    try {
      const r = await adminRunSettlementOnce();
      toast.success(`Worker: prepared ${r?.prepared ?? 0}, blocked ${r?.blocked ?? 0}`);
      load();
    } catch (e) { toast.error("Run failed"); }
    finally { setBusy(false); }
  };
  if (!dash) return <div className="text-sm text-zinc-500">Loading settlement ops…</div>;
  const g = dash.gates || {};
  return (
    <div className="space-y-4" data-testid="admin-settlement-ops">
      <div className="rounded-2xl border border-white/10 bg-[#080808] p-5">
        <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-400">Settlement gates</div>
        <div className="mt-3 grid gap-2 sm:grid-cols-2 font-mono text-xs">
          <div>enabled: {String(g.settlement_enabled)}</div>
          <div>dry_run: {String(g.dry_run)}</div>
          <div>broadcast: {String(g.broadcast_allowed)}</div>
          <div>block: {g.broadcast_block_reason || "—"}</div>
          <div>env: {g.app_env}</div>
          <div>prod_gate: {String(g.production_settlement_enabled)}</div>
        </div>
        <p className="mt-3 text-xs text-zinc-600">{g.disclaimer}</p>
      </div>
      <div className="rounded-2xl border border-white/10 bg-[#080808] p-5">
        <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-400">Queue</div>
        <div className="mt-3 grid gap-2 sm:grid-cols-3 font-mono text-sm">
          <div>pending {dash.pending_count ?? 0}</div>
          <div>broadcast {dash.broadcast_count ?? 0}</div>
          <div>failed {dash.failed_count ?? 0}</div>
        </div>
        <div className="mt-2 text-xs text-zinc-500">
          global_paused={String(dash.config?.global_settlement_paused)} · circuit={String(dash.config?.circuit_open)}
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <button disabled={busy} onClick={pause} data-testid="admin-settlement-pause" className="rounded-full border border-white/15 px-3 py-1.5 text-xs">Pause all</button>
          <button disabled={busy} onClick={resume} data-testid="admin-settlement-resume" className="rounded-full border border-white/15 px-3 py-1.5 text-xs">Resume</button>
          <button disabled={busy} onClick={runOnce} data-testid="admin-settlement-run" className="rounded-full border border-white/15 px-3 py-1.5 text-xs">Run once (dry-run)</button>
        </div>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [entities, setEntities] = useState([]);
  const [contribs, setContribs] = useState([]);

  useEffect(() => {
    if (admin === false) navigate("/admin/login");
  }, [admin, navigate]);

  const load = useCallback(async () => {
    try {
      const [s, e, c] = await Promise.all([adminStats(), adminEntities(), adminContributions()]);
      setStats(s); setEntities(e); setContribs(c);
    } catch (err) {}
  }, []);

  useEffect(() => { if (admin && admin.role === "admin") load(); }, [admin, load]);

  const doPause = async (id) => { await adminPause(id); toast.success("Updated"); load(); };
  const doDelete = async (id) => { if (!window.confirm("Delete this entity and all its contributions?")) return; await adminDelete(id); toast.success("Deleted"); load(); };
  const doVerify = async (id) => { await adminVerifyClaim(id); toast.success("Claim verified"); load(); };

  if (!admin || admin === false || !stats) {
    return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-zinc-500" /></div>;
  }

  const STAT_CARDS = [
    { label: "Total contributed", value: fmtUSD(stats.total_contributed), gold: true },
    { label: "24h volume", value: fmtUSD(stats.contributed_24h) },
    { label: "7d volume", value: fmtUSD(stats.contributed_7d) },
    { label: "Contributors", value: fmtNum(stats.contributors) },
    { label: "Contributions", value: fmtNum(stats.total_contributions) },
    { label: "Avg contribution", value: fmtUSDprecise(stats.average_contribution) },
    { label: "Largest contribution", value: fmtUSD(stats.largest_contribution) },
    { label: "Incentive Fund", value: fmtUSD(stats.house_incentives_usd || 0), hint: "Declared house seeding" },
    { label: "Entities", value: `${stats.entities_tokens}T · ${stats.entities_callers}C` },
  ];

  return (
    <div className="min-h-screen grain">
      <header className="sticky top-0 z-40 border-b border-white/10 bg-black/70 px-4 py-3 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center gap-3">
          <span className="font-display text-xl font-black tracking-tighter">FLIP</span>
          <span className="text-xs uppercase tracking-[0.2em] text-zinc-500">Admin</span>
          <button onClick={() => navigate("/")} className="ml-auto text-xs text-zinc-500 hover:text-white">View site</button>
          <button data-testid="admin-logout" onClick={() => { logout(); navigate("/admin/login"); }} className="flex items-center gap-1 rounded-full border border-white/15 px-3 py-1.5 text-xs hover:bg-white/5">
            <LogOut className="h-3.5 w-3.5" /> Logout
          </button>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-4 py-8">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="admin-stats">
          {STAT_CARDS.map((s) => (
            <div key={s.label} className="rounded-xl border border-white/10 bg-[#080808] px-4 py-3" title={s.hint || undefined}>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-600">{s.label}</div>
              <div className={`font-mono text-lg font-bold ${s.gold ? "text-gold" : "text-white"}`}>{s.value}</div>
            </div>
          ))}
        </div>

        <Tabs defaultValue="entities" className="mt-8">
          <TabsList className="bg-white/[0.03]">
            <TabsTrigger value="entities" data-testid="admin-tab-entities">Entities</TabsTrigger>
            <TabsTrigger value="contributions" data-testid="admin-tab-contributions">Contributions</TabsTrigger>
            <TabsTrigger value="hype" data-testid="admin-tab-hype">Hype Radar</TabsTrigger>
            <TabsTrigger value="verify" data-testid="admin-tab-verify">Verify TX</TabsTrigger>
            <TabsTrigger value="settings" data-testid="admin-tab-settings">Settings</TabsTrigger>
            <TabsTrigger value="flip" data-testid="admin-tab-flip">$FLIP</TabsTrigger>
            <TabsTrigger value="settlement" data-testid="admin-tab-settlement">Settlement</TabsTrigger>
            <TabsTrigger value="first-pilot" data-testid="admin-tab-first-pilot">First Pilot</TabsTrigger>
          </TabsList>

          <TabsContent value="entities" className="mt-4">
            <div className="overflow-hidden rounded-2xl border border-white/10 bg-[#080808]">
              {entities.map((e) => (
                <div key={e.id} className="flex items-center justify-between border-b border-white/5 px-4 py-3" data-testid={`admin-entity-${e.slug}`}>
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-sm text-zinc-500">#{e.rank}</span>
                    <div>
                      <div className="flex items-center gap-2 font-display font-bold">
                        {entityLabel(e)}
                        <span className="rounded border border-white/10 px-1.5 py-0.5 text-[9px] uppercase text-zinc-500">{e.category}</span>
                        {e.status === "paused" && <span className="rounded border border-negative/40 px-1.5 py-0.5 text-[9px] uppercase text-negative">Paused</span>}
                      </div>
                      <div className="font-mono text-xs text-zinc-600">{fmtUSD(e.total_backed_usd)} · {fmtNum(e.contributors)} backers</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {e.category === "callers" && !e.claimed && (
                      <button onClick={() => doVerify(e.id)} title="Verify claim" data-testid={`admin-verify-${e.slug}`} className="rounded-lg border border-white/15 p-2 hover:bg-white/5"><BadgeCheck className="h-4 w-4 text-positive" /></button>
                    )}
                    <button onClick={() => doPause(e.id)} title="Pause/Resume" data-testid={`admin-pause-${e.slug}`} className="rounded-lg border border-white/15 p-2 hover:bg-white/5">
                      {e.status === "paused" ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
                    </button>
                    <button onClick={() => doDelete(e.id)} title="Delete" data-testid={`admin-delete-${e.slug}`} className="rounded-lg border border-white/15 p-2 text-negative hover:bg-white/5"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="contributions" className="mt-4">
            <div className="overflow-hidden rounded-2xl border border-white/10 bg-[#080808]" data-testid="admin-contributions">
              {contribs.map((c) => {
                const row = formatAdminContributionRow(c);
                return (
                  <div key={c.id} className="flex items-center justify-between border-b border-white/5 px-4 py-3" data-testid={`admin-contribution-${c.id}`}>
                    <div>
                      <div className="font-mono text-sm">{row.wallet} → <span className="text-zinc-400">{row.token}</span></div>
                      <div className="font-mono text-[11px] text-zinc-600">{row.secondary}</div>
                    </div>
                    <span className="font-mono text-sm font-bold text-positive">{row.usdLabel}</span>
                  </div>
                );
              })}
              {contribs.length === 0 && <div className="px-4 py-12 text-center text-zinc-600">No contributions yet</div>}
            </div>
          </TabsContent>

          <TabsContent value="hype" className="mt-4">
            <HypeRadarPanel />
          </TabsContent>

          <TabsContent value="verify" className="mt-4">
            <VerifyTxPanel />
          </TabsContent>

          <TabsContent value="settings" className="mt-4">
            <SettingsPanel />
          </TabsContent>

          <TabsContent value="flip" className="mt-4">
            <FlipUtilityPanel />
          </TabsContent>

          <TabsContent value="settlement" className="mt-4">
            <SettlementOpsPanel />
          </TabsContent>

          <TabsContent value="first-pilot" className="mt-4">
            <FirstLivePilotPanel />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
